import {
  HappycoResidentFragment,
  HappycoResidentsQuery,
  HappycoResidentsQueryVariables,
} from "gql/graphql";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Select, SingleValue } from "chakra-react-select";
import { SystemStyleObject } from "@chakra-ui/react";
import colors from "app/colors";
import HAPPYCO_RESIDENTS_QUERY from "./_networking/happyco-residents";
import { SELECT_MENU_PORTAL_Z_INDEX } from "modules/providers/chakra-provider/chakra-portal-stack";

interface IProps {
  propertyId?: string;
  unitId?: string;
  onResidentSelected: (
    happycoResident?: SingleValue<HappycoResidentFragment>,
  ) => void;
}

const HappycoResidentSelector = ({
  propertyId,
  unitId,
  onResidentSelected,
}: IProps) => {
  const { data } = useQuery<
    HappycoResidentsQuery,
    HappycoResidentsQueryVariables
  >(HAPPYCO_RESIDENTS_QUERY, {
    variables: propertyId ? { propertyId, unitId } : undefined,
    skip: !propertyId,
  });

  const [value, setValue] =
    React.useState<SingleValue<HappycoResidentFragment> | null>();

  useEffect(() => {
    setValue(null);
    onResidentSelected(undefined);
  }, [onResidentSelected, propertyId, unitId]);

  const onChange = (happycoResident?: SingleValue<HappycoResidentFragment>) => {
    setValue(happycoResident);
    onResidentSelected(happycoResident);
  };

  const getOptionLabel = useCallback(
    (happycoResident: HappycoResidentFragment) => happycoResident.name,
    [],
  );

  const getOptionValue = useCallback(
    (happycoResident: HappycoResidentFragment) => happycoResident.happycoId,
    [],
  );

  const happycoResidents = useMemo(() => data?.happycoResidents || [], [data]);

  return (
    <Select
      options={happycoResidents}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      placeholder="Select Resident"
      noOptionsMessage={() => "No residents found for the selected property"}
      onChange={onChange}
      value={value}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
      }}
      chakraStyles={{
        placeholder: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          fontStyle: "italic",
          color: colors.placeholderText,
          fontSize: 14,
        }),
        container: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          width: "full",
          borderColor: colors.grayPrimary,
        }),
        control: (provided: SystemStyleObject): SystemStyleObject => ({
          ...provided,
          minHeight: "34px",
        }),
      }}
      menuPortalTarget={document.body}
      styles={{
        menuPortal: (base) => ({
          ...base,
          zIndex: SELECT_MENU_PORTAL_Z_INDEX,
        }),
      }}
    />
  );
};

export default memo(HappycoResidentSelector);
